import { FUND_STORAGE_KEYS } from "@core/constants";
import {
  columns,
  FUND_COLUMN_DEFAULT_VISIBILITY,
} from "@funds/components/column";
import { FILTER_CONFIG, FilterConfig } from "@funds/config";
import { VisibilityState } from "@tanstack/react-table";
import { useMemo } from "react";
import { useLocalStorage } from "usehooks-ts";

export function useFundsTableConfig() {
  const [columnOrder, setColumnOrder] = useLocalStorage<string[]>(
    FUND_STORAGE_KEYS.ColumnOrder,
    columns.map((c) => c.id as string)
  );

  const [columnVisibility, setColumnVisibility] =
    useLocalStorage<VisibilityState>(
      FUND_STORAGE_KEYS.ColumnVisibility,
      FUND_COLUMN_DEFAULT_VISIBILITY
    );

  const [filterConfig, setFilterConfig] = useLocalStorage<
    Partial<FilterConfig>[]
  >(
    FUND_STORAGE_KEYS.FilterVisibility,
    FILTER_CONFIG.map((fc) => ({ id: fc.id, visible: fc.visible }))
  );

  const [filtersOrder, setFiltersOrder] = useLocalStorage<string[]>(
    FUND_STORAGE_KEYS.FilterOrder,
    []
  );

  const sortedFiltersConfig = useMemo(
    () =>
      FILTER_CONFIG.map((f) => {
        const overrides = filterConfig.find((fc) => fc.id === f.id);
        return { ...f, ...overrides };
      }).toSorted(
        (a, b) => filtersOrder.indexOf(a.id) - filtersOrder.indexOf(b.id)
      ) as FilterConfig[],
    [filterConfig, filtersOrder]
  );

  return useMemo(
    () => ({
      columnVisibility,
      onColumnVisibilityChange: setColumnVisibility,
      columnOrder,
      onColumnOrderChange: setColumnOrder,
      filterConfig: sortedFiltersConfig,
      setFilterConfig,
      filtersOrder,
      onFiltersOrderChange: setFiltersOrder,
    }),
    [
      columnVisibility,
      setColumnVisibility,
      setColumnOrder,
      columnOrder,
      sortedFiltersConfig,
      setFilterConfig,
      filtersOrder,
      setFiltersOrder,
    ]
  );
}
